/**
 * Checkout upsell — the pre-checkout offer for the 1-year app subscription.
 *
 * Live (WordPress) auto-opens a Spectra popup ~500ms after the FunnelKit checkout page loads,
 * because it can inject into its own checkout. Shopify checkout is not ours to touch below Plus,
 * so LUM-39 moved the trigger to the side cart's Checkout button — the last moment we own. The
 * modal's copy, layout and Add/Skip behaviour are ported from live; only the trigger moved.
 *
 * Interception is capture-phase for the same reason the photosensitivity gate's is: Horizon
 * delegates its `on:click` bindings from a bubble-phase listener on `document`, so stopping the
 * event during capture is what keeps the cart form from submitting underneath us.
 *
 * Continuing to checkout uses `form.requestSubmit(button)` rather than re-clicking the button.
 * That submits natively *with* the submitter's `name="checkout"` in the payload — which is what
 * routes the POST to checkout — while dispatching no click, so this listener cannot re-enter and
 * loop.
 */

const CHECKOUT_BUTTON = 'button[name="checkout"]';
// The cart panel from LUM-38. Its presence is the server-rendered answer to "is the offer
// available?" — it renders only when the feature is on, the cart is not empty, the product
// resolves and is purchasable, and it is not already in the cart. Those are exactly the
// conditions for showing this modal, it lives inside the drawer's hydration key so it is never
// stale, and reading it costs one DOM query instead of a round trip to /cart.js on a click the
// customer is waiting on. The coupling is deliberate: one offer, one switch.
const OFFER_AVAILABLE = '.cart-upsell';
// Rendered by cart-summary when checkout is blocked (digital-only basket, or a line outside the
// current market). The Checkout button is disabled then, so the modal never opens from it — this
// is re-checked before submitting in case the cart re-rendered into that state while it was open.
const CART_BLOCKED = '[data-cart-blocked]';

class CheckoutUpsell extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #pendingButton = null;
  #busy = false;

  connectedCallback() {
    document.addEventListener('click', this.#interceptCheckout, { capture: true });
    this.addEventListener('click', this.#handleModalClick);
    // Escape and a backdrop click close the dialog without going through Add or Skip. The
    // customer's Checkout click was swallowed to open this modal, so dismissing it any other way
    // has to still take them to checkout — otherwise the click just vanishes and they are stuck
    // staring at the cart. Dismissing means declining, same as Skip.
    // Bound on the dialog-component itself, not on `this`: DialogCloseEvent is constructed
    // without `bubbles`, so it never reaches an ancestor.
    this.#dialog?.addEventListener('dialog:close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#interceptCheckout, { capture: true });
  }

  get #dialog() {
    return /** @type {any} */ (this.querySelector('dialog-component'));
  }

  get #variantId() {
    return this.dataset.variantId;
  }

  /** @param {MouseEvent} event */
  #interceptCheckout = (event) => {
    if (this.#busy || !this.#variantId) return;

    const target = event.target instanceof Element ? event.target : null;
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest(CHECKOUT_BUTTON));
    if (!button || button.disabled) return;

    // No offer to make — let the checkout submit run untouched.
    if (!document.querySelector(OFFER_AVAILABLE)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.#pendingButton = button;
    this.#open();
  };

  async #open() {
    const dialog = this.#dialog;
    if (!dialog) {
      // Nothing to show — don't strand the customer on the cart.
      this.#continueToCheckout();
      return;
    }

    // dialog.js ships with a low fetch priority, so the element may not be upgraded yet.
    await customElements.whenDefined('dialog-component');
    dialog.showDialog();
  }

  /** @param {MouseEvent} event */
  #handleModalClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-checkout-upsell-add]')) {
      event.preventDefault();
      this.#addThenCheckout();
    } else if (target.closest('[data-checkout-upsell-skip]')) {
      event.preventDefault();
      this.#continueToCheckout();
    }
  };

  // Both decision paths clear #pendingButton before they await closeDialog(), so the close they
  // trigger themselves finds nothing pending and this is a no-op for them.
  #handleDialogClose = () => {
    if (this.#busy || !this.#pendingButton) return;
    this.#continueToCheckout();
  };

  /**
   * Add the subscription, then continue. This posts to the cart API directly instead of routing
   * through `product-form-component`: that component's job is to add *and* re-render the cart
   * drawer, and re-rendering the drawer we are about to navigate away from would only race the
   * navigation — the section it morphs is the one holding this modal's trigger.
   */
  async #addThenCheckout() {
    if (this.#busy) return;
    this.#busy = true;
    this.#setPending(true);

    const addUrl = /** @type {any} */ (globalThis).Theme?.routes?.cart_add_url ?? '/cart/add';

    try {
      const response = await fetch(`${addUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: this.#variantId, quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
    } catch (error) {
      // A failed add must not trap the customer in the modal — send them to checkout with what
      // they already chose, which is the same outcome as skipping.
      console.error(error);
    }

    this.#continueToCheckout();
  }

  async #continueToCheckout() {
    this.#busy = true;

    const button = this.#pendingButton;
    this.#pendingButton = null;

    await this.#dialog?.closeDialog?.();

    if (document.querySelector(CART_BLOCKED)) {
      this.#busy = false;
      return;
    }

    const form = button?.form;
    if (form && button) {
      form.requestSubmit(button);
    } else {
      // The Checkout button always carries `form="cart-form"`, but never leave the customer
      // stuck if that changes.
      globalThis.location.assign(/** @type {any} */ (globalThis).Theme?.routes?.cart_url ?? '/cart');
    }
  }

  /** @param {boolean} pending */
  #setPending(pending) {
    for (const el of this.querySelectorAll('[data-checkout-upsell-add], [data-checkout-upsell-skip]')) {
      /** @type {HTMLButtonElement} */ (el).disabled = pending;
    }
    this.toggleAttribute('data-pending', pending);
  }
}

if (!customElements.get('checkout-upsell')) {
  customElements.define('checkout-upsell', CheckoutUpsell);
}
